import React from 'react';

const MAX_HTTP_REQUESTS = 20;
const MAX_DESCRIPTION_LENGTH = 32000;
const MAX_RESPONSE_TEXT_LENGTH = 4000;
const COLLAPSABLE_PREFIX = '{{collapse(Show logs,Hide logs)';
const COLLAPSABLE_SUFFIX = '}}';
const MAX_COLLAPSABLE_TEXT_LENGTH = Math.max(
  0,
  MAX_DESCRIPTION_LENGTH - COLLAPSABLE_PREFIX.length - COLLAPSABLE_SUFFIX.length,
);

export interface HttpRequestDiagnostic {
  method: string;
  path: string;
  status: string;
  durationMs: number | null;
  time: string;
  responseText?: string;
}

const httpRequestHistory: HttpRequestDiagnostic[] = [];

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeMultilineHtml = (value: string): string => escapeHtml(value).replace(/\n/g, '<br>');

const normalizeRequestPath = (value: string): string => {
  try {
    const base = window?.location?.origin || 'https://davinci.local';
    const parsed = new URL(value, base);
    return parsed.pathname || 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

const safePagePath = (): string => {
  try {
    return window.location.pathname || 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

const safeUserAgent = (): string => {
  try {
    return window.navigator.userAgent || 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

const safeOnline = (): string => {
  try {
    return String(window.navigator.onLine);
  } catch (error) {
    return 'unknown';
  }
};

const safeViewport = (): string => {
  try {
    return `${window.innerWidth}x${window.innerHeight}`;
  } catch (error) {
    return 'unknown';
  }
};

const normalizeComponentStack = (stack: string | undefined): string => {
  if (!stack) {
    return 'unknown';
  }

  return stack
    .trim()
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .slice(0, 25)
    .join(' | ');
};

type SupportDescriptionContext = {
  error?: Error;
  info?: React.ErrorInfo;
};

export const recordHttpRequestDiagnostic = (entry: {
  method: string;
  path: string;
  status: number | string;
  durationMs: number | null;
  time: string;
  responseText?: string;
}) => {
  const normalized: HttpRequestDiagnostic = {
    method: entry.method,
    path: normalizeRequestPath(entry.path),
    status: String(entry.status),
    durationMs: entry.durationMs,
    time: entry.time,
    responseText: entry.responseText?.slice(0, MAX_RESPONSE_TEXT_LENGTH),
  };

  httpRequestHistory.unshift(normalized);

  if (httpRequestHistory.length > MAX_HTTP_REQUESTS) {
    httpRequestHistory.length = MAX_HTTP_REQUESTS;
  }
};

export const getHttpRequestHistory = (): HttpRequestDiagnostic[] => {
  return [...httpRequestHistory];
};

export const formatSupportDescription = ({ error, info }: SupportDescriptionContext = {}): string => {
  const requestHistory = getHttpRequestHistory();
  const stack = normalizeComponentStack(info?.componentStack || undefined);
  const requestHistoryText = requestHistory.length
    ? requestHistory
        .map(request =>
          [
            `- ${escapeHtml(request.time)} | ${escapeHtml(request.method)} ${escapeHtml(request.path)} | status ${escapeHtml(
              request.status,
            )} | duration ${escapeHtml(request.durationMs === null ? 'unknown' : `${request.durationMs}ms`)}`,
            request.responseText ? `<br>response text:<br>${escapeMultilineHtml(request.responseText)}` : '',
          ].join(''),
        )
        .join('<br>')
    : '- no recent requests captured';

  const text = [
    '<p></p><p></p><p></p>',
    error ? `<p>Error: ${escapeHtml(error.name || 'Error')} - ${escapeHtml(error.message || 'unknown')}</p>` : '',
    `<p>Location: ${escapeHtml(safePagePath())}</p>`,
    `<p>Online: ${escapeHtml(safeOnline())}</p>`,
    `<p>Time: ${new Date().toISOString()}</p>`,
    `<p>User-agent: ${escapeHtml(safeUserAgent())}</p>`,
    `<p>Viewport: ${escapeHtml(safeViewport())}</p>`,
    info ? `<p>Component stack trace: ${escapeHtml(stack)}</p>` : '',
    `<p>Recent HTTP requests:<br>${requestHistoryText}</p>`,
  ].join('');

  return `${COLLAPSABLE_PREFIX}${text.slice(0, MAX_COLLAPSABLE_TEXT_LENGTH)}${COLLAPSABLE_SUFFIX}`;
};

export const formatErrorSupportDescription = (error: Error, info: React.ErrorInfo): string => {
  return formatSupportDescription({ error, info });
};

export const resetHttpRequestHistoryForTests = () => {
  httpRequestHistory.length = 0;
};
